import type {
    MappedRentalBody,
    MappedRentalDetail,
    MappedRentalPrice,
    MappedRentalProduct,
    ProductDetailRentalApi,
    ProductStickerApi,
    RentalProductApiResponses,
    RentalProductPart,
} from '#/global/service/ProductApiTypes'

import { Config } from '#/global/helper/Config'

const buildImageUrl = (image: number) => {
    const cdn = Config.getBaseCdn().replace(/\/$/, '')
    return `${cdn}/${Config.getShopId()}/img/product-rental/${image}.webp`
}

const mapDetail = (detail: ProductDetailRentalApi): MappedRentalDetail => ({
    productCode: detail.productCode,
    title: detail.title,
    titleShort: detail.titleShort,
    slug: detail.slug,
    categoryId: detail.categoryId,
    isFeatured: detail.isFeatured,
    image: detail.image === null ? null : buildImageUrl(detail.image),
    descriptionShort: detail.descriptionShort,
    priceVatOrigin: detail.priceVatGroupStarting,
    priceNoVatOrigin: detail.priceNoVatGroupStarting,
    unitQuantity: detail.unitQuantity,
    unitPrice: detail.unitPrice,
    currencyCode: detail.currencyCode,
    currencySymbol: detail.currencySymbol,
})

const mapPrice = (detail: ProductDetailRentalApi): MappedRentalPrice => ({
    priceVatOrigin: detail.priceVatGroupStarting,
    priceNoVatOrigin: detail.priceNoVatGroupStarting,
    unitQuantity: detail.unitQuantity,
    unitPrice: detail.unitPrice,
    hasGuarantee: detail.hasGuarantee,
    priceVatGuarantee: detail.priceVatGuarantee,
    priceNoVatGuarantee: detail.priceNoVatGuarantee,
    unitGuarantee: detail.unitGuarantee,
    discountPercent: detail.discountPercent,
    currencyCode: detail.currencyCode,
    currencySymbol: detail.currencySymbol,
})

const mapSticker = (sticker: ProductStickerApi) => ({
    id: sticker.id,
    image: buildImageUrl(sticker.image),
    isVisibleInCategory: sticker.isVisibleInCategory,
    ...(sticker.tooltip == null ? {} : { tooltip: sticker.tooltip }),
})

const mapPart = (
    product: MappedRentalBody,
    part: RentalProductPart,
    detail: ProductDetailRentalApi,
    index: number,
    responses: RentalProductApiResponses,
) => {
    switch (part) {
        case 'detail':
            product.detail = mapDetail(detail)
            break
        case 'price':
            product.price = mapPrice(detail)
            break
        case 'sticker':
            product.sticker = (responses.sticker?.[index]?.stickers ?? []).map(sticker => mapSticker(sticker))
            break
    }
}

/** Map positional rental API responses into the legacy UI product shape. */
export function mapRentalProducts<T extends RentalProductPart>(
    codes: readonly string[],
    parts: readonly T[],
    responses: RentalProductApiResponses,
): Array<MappedRentalProduct<T> | null>
export function mapRentalProducts(
    codes: readonly string[],
    parts: readonly RentalProductPart[],
    responses: RentalProductApiResponses,
): Array<MappedRentalBody | null> {
    return codes.map((code, index) => {
        const detail = responses.detail?.[index] ?? null
        if (detail === null) {
            return null
        }

        const product: MappedRentalBody = {
            code,
            type: 'rental',
            groupCode: detail.groupCode,
            groupId: detail.groupId,
            productCode: detail.productCode,
            productId: detail.productId,
        }
        for (const part of parts) {
            mapPart(product, part, detail, index, responses)
        }

        return product
    })
}
